(function () {
  'use strict';

  window.OatmealStudioMount = function OatmealStudioMount(root) {
    if (!root) return;
    if (root.dataset.studioMounted === '1') return;
    root.dataset.studioMounted = '1';

    function q(sel, ctx) { return (ctx || root).querySelector(sel); }
    function qa(sel, ctx) { return Array.from((ctx || root).querySelectorAll(sel)); }

    function safeParseJson(el, label) {
      if (!el) {
        console.error('[OATMEAL STUDIO] JSON element missing:', label);
        return [];
      }
      const txt = el.textContent || '';
      try {
        const parsed = JSON.parse(txt || '[]');
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        console.error('[OATMEAL STUDIO] JSON parse failed for', label, e);
        return [];
      }
    }

    function escapeHtml(str) {
      return String(str ?? '').replace(/[&<>"']/g, s => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
      }[s]));
    }

    function escapeAttr(str) {
      return escapeHtml(str).replace(/`/g, '&#96;');
    }

    function lockPageScroll() {
      const body = document.body;
      const doc = document.documentElement;
      const count = Number(window.__oatmealStudioScrollLockCount || 0);

      window.__oatmealStudioScrollLockCount = count + 1;
      if (count > 0) return;

      body.classList.add('oatmeal-studio-scroll-locked');
      doc.classList.add('oatmeal-studio-scroll-locked');
    }

    function unlockPageScroll() {
      const body = document.body;
      const doc = document.documentElement;
      const count = Number(window.__oatmealStudioScrollLockCount || 0);
      if (count <= 0) return;

      const nextCount = count - 1;
      window.__oatmealStudioScrollLockCount = nextCount;
      if (nextCount > 0) return;

      body.classList.remove('oatmeal-studio-scroll-locked');
      doc.classList.remove('oatmeal-studio-scroll-locked');
    }

    function normalizeHex(hex) {
      if (!hex) return null;
      let h = String(hex).trim();
      if (!h || h.toLowerCase() === 'null') return null;
      if (/^[0-9a-fA-F]{6}$/.test(h)) h = '#' + h;
      if (!/^#[0-9a-fA-F]{6}$/.test(h)) return null;
      return h;
    }

    function formatMoney(cents) {
      return '₹' + (Number(cents || 0) / 100).toFixed(2).replace(/\.00$/, '');
    }

    function formatHotspotMoney(variant) {
      const cents = Number(variant && variant.price);
      if (Number.isFinite(cents)) {
        return '₹' + new Intl.NumberFormat('en-IN', {
          maximumFractionDigits: 0
        }).format(cents / 100);
      }

      const fallback = String(variant && variant.price_formatted || '').trim();
      if (!fallback) return '';
      return fallback.replace(/\.\d+\b/, '');
    }

    function slugify(s) {
      return String(s || '')
        .trim()
        .toLowerCase()
        .replace(/&/g, 'and')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    }

    function inferCategoryFromProduct(product) {
      const pt = String((product && product.product_type) || '').trim().toLowerCase();
      if (pt.includes('upper wear')) return 'upper';
      if (pt.includes('lower wear')) return 'bottom';
      return null;
    }

    function ensureInjectedProduct(category, productPayload) {
      if (!category || !productPayload || !productPayload.id) return null;
      if (!productsData[category]) productsData[category] = [];

      const bucket = productsData[category];
      let existing = bucket.find(p => String(p.id) === String(productPayload.id));
      if (!existing) {
        bucket.unshift(productPayload);
        existing = productPayload;
        globalColors[category] = buildGlobalColors(bucket);
      }

      return existing;
    }

    const activeToneBtn = q('.oatmeal-studio__skin-tone.is-active,[data-skin-tone].is-active');
    const activeShapeBtn = q('.oatmeal-studio__body-shape.is-active,[data-body-shape].is-active');

    const state = {
      isDarkMode: false,
      skinTone: (activeToneBtn && activeToneBtn.getAttribute('data-skin-tone')) ? String(activeToneBtn.getAttribute('data-skin-tone')) : '1',
      bodyShape: (activeShapeBtn && activeShapeBtn.getAttribute('data-body-shape')) ? String(activeShapeBtn.getAttribute('data-body-shape')) : 'M',
      upper: { product: null, variant: null },
      bottom: { product: null, variant: null },
      pending: {
        upper: { product: null, variant: null },
        bottom: { product: null, variant: null }
      },
      currentModal: null,
      selectedProduct: null,
      filters: {
        upper: { style: 'all', print: 'all' },
        bottom: { style: 'all', print: 'all' }
      },
      page: { upper: 1, bottom: 1 },
      pageSize: 8
    };

    const upperDataEl = q('script[type="application/json"][data-studio-products="upper"]');
    const bottomDataEl = q('script[type="application/json"][data-studio-products="bottom"]');

    const productsData = {
      upper: safeParseJson(upperDataEl, 'upper'),
      bottom: safeParseJson(bottomDataEl, 'bottom')
    };

    function buildGlobalColors(products) {
      const map = new Map();
      (products || []).forEach(p => {
        (p.variants || []).forEach(v => {
          const name = String(v.option1 || '').trim();
          if (!name) return;
          const hex = normalizeHex(v.colour_hex);
          if (!map.has(name)) map.set(name, { name, hex });
        });
      });
      return Array.from(map.values());
    }

    const globalColors = {
      upper: buildGlobalColors(productsData.upper),
      bottom: buildGlobalColors(productsData.bottom)
    };

    const HOTSPOT_DOT_RULES = {
      upper: { x: 0.42, y: 0.23 },
      bottom: { x: 0.60, y: 0.72 }
    };

    const HOTSPOT_LAYOUTS = {
      mobile: {
        upper: {
          dot: { mode: 'rule', ruleKey: 'upper' },
          callout: {
            mode: 'computed',
            direction: 'upper-left',
            gapX: 14,
            gapY: 12,
            biasX: 18,
            edgePad: 10,
            bottomPad: 12,
            edgeInsetX: 8,
            edgeInsetY: 10
          }
        },
        bottom: {
          dot: { mode: 'fixed', x: 59.13, y: 68.71 },
          callout: { mode: 'fixed', lx: 22, ly: 14, lineLx: 14, lineLy: 22 }
        }
      },
      desktop: {
        upper: {
          dot: { mode: 'rule', ruleKey: 'upper' },
          callout: {
            mode: 'computed',
            direction: 'upper-left',
            gapX: 24,
            gapY: 20,
            biasX: 0,
            edgePad: 16,
            bottomPad: 8,
            edgeInsetX: 10,
            edgeInsetY: 12
          }
        },
        bottom: {
          dot: { mode: 'rule', ruleKey: 'bottom' },
          callout: {
            mode: 'computed',
            direction: 'lower-right',
            gapX: 24,
            gapY: 20,
            biasX: 0,
            edgePad: 16,
            bottomPad: 8,
            edgeInsetX: 10,
            edgeInsetY: 12
          }
        }
      }
    };

    const elements = {
      buyLookBtn: q('[data-buy-look]'),
      themeToggle: q('[data-studio-theme-toggle]'),
      previewImage: q('[data-preview-image]'),
      previewDots: q('[data-preview-dots]'),
      previewNav: q('[data-preview-nav]'),
      previewPlaceholder: q('.oatmeal-studio__preview-placeholder'),
      previewViewer: q('[data-preview-viewer]'),
      viewerStage: q('[data-viewer-stage]'),
      viewerImage: q('[data-viewer-image]'),
      toast: q('[data-toast]'),
      modals: {
        upper: q('[data-modal="upper"]'),
        bottom: q('[data-modal="bottom"]')
      },
      hotspots: {
        upper: q('[data-hotspot="upper"]'),
        bottom: q('[data-hotspot="bottom"]')
      }
    };

    const viewerState = {
      open: false,
      scale: 1,
      x: 0,
      y: 0,
      baseWidth: 0,
      baseHeight: 0,
      pointers: new Map(),
      dragOriginX: 0,
      dragOriginY: 0,
      pinchDistance: 0,
      pinchScale: 1
    };

    function syncThemeMode() {
      root.classList.toggle('is-dark-mode', state.isDarkMode);
      Object.keys(elements.modals).forEach(type => {
        const modal = elements.modals[type];
        if (!modal) return;
        modal.classList.toggle('is-dark-mode', state.isDarkMode);
      });
      if (elements.previewViewer) {
        elements.previewViewer.classList.toggle('is-dark-mode', state.isDarkMode);
      }
      if (!elements.themeToggle) return;

      elements.themeToggle.setAttribute('aria-pressed', state.isDarkMode ? 'true' : 'false');
      elements.themeToggle.setAttribute(
        'aria-label',
        state.isDarkMode ? 'Turn the studio lights on' : 'Turn the studio lights off'
      );
    }

    function mountModalsToBody() {
      Object.keys(elements.modals).forEach(type => {
        const modal = elements.modals[type];
        if (modal && modal.parentNode !== document.body) {
          document.body.appendChild(modal);
        }
      });
      if (elements.previewViewer && elements.previewViewer.parentNode !== document.body) {
        document.body.appendChild(elements.previewViewer);
      }
    }

    function getDefaultTabLabel(type) {
      return type === 'upper'
        ? 'PICK<span style="display:block; margin-top:6px;">YOUR TOP</span>'
        : 'PICK<span style="display:block; margin-top:6px;">YOUR BOTTOM</span>';
    }

    function setHasPreview(hasPreview) {
      root.classList.toggle('has-preview', !!hasPreview);
    }

    function clamp(num, min, max) {
      return Math.min(max, Math.max(min, num));
    }

    function setPreviewImageInteractive(enabled) {
      if (!elements.previewImage) return;
      elements.previewImage.tabIndex = enabled ? 0 : -1;
      elements.previewImage.setAttribute('aria-hidden', enabled ? 'false' : 'true');
    }

    function resetViewerTransform() {
      viewerState.scale = 1;
      viewerState.x = 0;
      viewerState.y = 0;
      if (elements.viewerStage) elements.viewerStage.classList.remove('is-dragging');
      if (elements.viewerImage) {
        elements.viewerImage.style.transform = 'translate(0px, 0px) scale(1)';
      }
    }

    function getViewerBaseSize() {
      if (!viewerState.baseWidth || !viewerState.baseHeight) return null;
      return { width: viewerState.baseWidth, height: viewerState.baseHeight };
    }

    function fitViewerImageToStage() {
      const stage = elements.viewerStage;
      const img = elements.viewerImage;
      if (!stage || !img) return;

      const stageRect = stage.getBoundingClientRect();
      const naturalWidth = img.naturalWidth;
      const naturalHeight = img.naturalHeight;
      if (!stageRect.width || !stageRect.height || !naturalWidth || !naturalHeight) return;

      const stageRatio = stageRect.width / stageRect.height;
      const imageRatio = naturalWidth / naturalHeight;
      let width;
      let height;

      if (imageRatio > stageRatio) {
        width = stageRect.width;
        height = width / imageRatio;
      } else {
        height = stageRect.height;
        width = height * imageRatio;
      }

      viewerState.baseWidth = width;
      viewerState.baseHeight = height;
      img.style.width = Math.max(1, Math.floor(width)) + 'px';
      img.style.height = Math.max(1, Math.floor(height)) + 'px';
    }

    function clampViewerPan() {
      if (viewerState.scale <= 1) {
        viewerState.x = 0;
        viewerState.y = 0;
        return;
      }

      const base = getViewerBaseSize();
      if (!base) return;

      const maxX = Math.max(0, (base.width * viewerState.scale - base.width) / 2);
      const maxY = Math.max(0, (base.height * viewerState.scale - base.height) / 2);
      viewerState.x = clamp(viewerState.x, -maxX, maxX);
      viewerState.y = clamp(viewerState.y, -maxY, maxY);
    }

    function applyViewerTransform() {
      if (!elements.viewerImage) return;
      clampViewerPan();
      elements.viewerImage.style.transform =
        'translate(' + Math.round(viewerState.x) + 'px, ' + Math.round(viewerState.y) + 'px) scale(' + viewerState.scale.toFixed(3) + ')';
    }

    function setViewerScale(nextScale) {
      viewerState.scale = clamp(nextScale, 1, 4);
      if (viewerState.scale <= 1) {
        viewerState.x = 0;
        viewerState.y = 0;
        if (elements.viewerStage) elements.viewerStage.classList.remove('is-dragging');
      }
      applyViewerTransform();
    }

    function zoomViewer(direction) {
      const factor = direction > 0 ? 1.2 : 1 / 1.2;
      setViewerScale(viewerState.scale * factor);
    }

    function closePreviewViewer() {
      if (!viewerState.open || !elements.previewViewer) return;
      viewerState.open = false;
      viewerState.pointers.clear();
      elements.previewViewer.classList.remove('is-open');
      if (elements.viewerStage) elements.viewerStage.classList.remove('is-dragging');
      unlockPageScroll();
      resetViewerTransform();
    }

    function openPreviewViewer() {
      if (!elements.previewImage || !elements.previewViewer || !elements.viewerImage) return;
      const src = elements.previewImage.currentSrc || elements.previewImage.getAttribute('src') || '';
      const isVisible = elements.previewImage.classList.contains('is-visible');
      if (!src || !isVisible) return;

      viewerState.open = true;
      viewerState.pointers.clear();
      if (elements.viewerImage.getAttribute('src') !== src) {
        elements.viewerImage.src = src;
      }
      elements.viewerImage.alt = elements.previewImage.getAttribute('alt') || 'Zoomed preview result';
      elements.previewViewer.classList.add('is-open');
      lockPageScroll();
      viewerState.baseWidth = 0;
      viewerState.baseHeight = 0;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!viewerState.open) return;
          fitViewerImageToStage();
          resetViewerTransform();
        });
      });
    }

    function getPointerDistance(points) {
      const [a, b] = points;
      if (!a || !b) return 0;
      const dx = a.x - b.x;
      const dy = a.y - b.y;
      return Math.hypot(dx, dy);
    }

    // Preview carousel: front/back views of the same upper+lower+skin+body combo.
    const STUDIO_VIEWS = ['front', 'back'];

    // ===========================================================================
    // TESTING MODE — REMOVE BEFORE PUBLIC LAUNCH. See assets/OATMEAL-STUDIO-README.md
    // While S3 holds only one sample render, every upper/bottom selection shows
    // that single image (for both front & back views) and logs the presigned URL
    // to the browser console. To GO LIVE: set STUDIO_TESTING = false — onStateChange
    // then resolves the real per-combo keys from buildStudioKeyFromState().
    // ===========================================================================
    // Set to false before production launch.
    const STUDIO_TESTING = true;
    const STUDIO_TEST_KEY = 'Garment-Images/skin2_athletic_SBType2_AB5944_YPFlaire_AB5944.png';
    let previewCarousel = { urls: [], index: 0 };
    let previewSwiped = false; // set by a swipe so the trailing click doesn't open zoom

    function resetPreview() {
      closePreviewViewer();
      setHasPreview(false);
      if (elements.previewImage) {
        elements.previewImage.classList.remove('is-visible');
        elements.previewImage.removeAttribute('src');
      }
      previewCarousel = { urls: [], index: 0 };
      if (elements.previewDots) {
        elements.previewDots.innerHTML = '';
        elements.previewDots.style.display = 'none';
      }
      if (elements.previewNav) elements.previewNav.hidden = true;
      setPreviewImageInteractive(false);
      if (elements.previewPlaceholder) elements.previewPlaceholder.style.display = 'flex';
      if (elements.hotspots.upper) elements.hotspots.upper.classList.remove('is-visible');
      if (elements.hotspots.bottom) elements.hotspots.bottom.classList.remove('is-visible');
    }

    function renderPreviewImage(url) {
      const img = elements.previewImage;
      const ph = elements.previewPlaceholder;
      if (!img || !ph) return;

      setHasPreview(false);
      ph.style.display = 'none';
      img.classList.remove('is-visible');

      if (elements.hotspots.upper) elements.hotspots.upper.classList.remove('is-visible');
      if (elements.hotspots.bottom) elements.hotspots.bottom.classList.remove('is-visible');

      img.onload = () => {
        img.classList.add('is-visible');
        setHasPreview(true);
        setPreviewImageInteractive(true);
        if (elements.viewerImage) {
          elements.viewerImage.src = url;
          elements.viewerImage.alt = img.getAttribute('alt') || 'Zoomed preview result';
        }
        refreshPreviewHotspots();
      };

      img.onerror = resetPreview;
      img.src = url;
    }

    function renderPreviewDots() {
      const dots = elements.previewDots;
      if (!dots) return;
      const n = previewCarousel.urls.length;
      if (elements.previewNav) elements.previewNav.hidden = n <= 1;
      if (n <= 1) {
        dots.innerHTML = '';
        dots.style.display = 'none';
        return;
      }
      dots.style.display = '';
      dots.innerHTML = previewCarousel.urls.map(function (_, i) {
        return '<button type="button" class="oatmeal-studio__preview-dot' +
          (i === previewCarousel.index ? ' is-active' : '') +
          '" data-preview-dot="' + i + '" aria-label="' +
          (STUDIO_VIEWS[i] || ('view ' + (i + 1))) + ' view"></button>';
      }).join('');
    }

    function setPreviewView(i) {
      const n = previewCarousel.urls.length;
      if (!n) return;
      previewCarousel.index = ((i % n) + n) % n;
      renderPreviewImage(previewCarousel.urls[previewCarousel.index]);
      renderPreviewDots();
    }

    function renderPreviewCarousel(urls) {
      previewCarousel.urls = (urls || []).filter(Boolean);
      previewCarousel.index = 0;
      if (!previewCarousel.urls.length) {
        resetPreview();
        return;
      }
      renderPreviewImage(previewCarousel.urls[0]);
      renderPreviewDots();
    }

    async function resolveAndRenderViews(keys) {
      try {
        const urls = await Promise.all(keys.map(getPresignedUrl));
        renderPreviewCarousel(urls);
      } catch (err) {
        console.error('[OATMEAL STUDIO] resolve failed', keys, err);
        resetPreview();
      }
    }

    function refreshPreviewHotspots() {
      const hasPreview = elements.previewImage && elements.previewImage.classList.contains('is-visible');
      if (!hasPreview) return;
      positionDotFromImage('upper');
      positionDotFromImage('bottom');
      updateHotspot('upper');
      updateHotspot('bottom');
    }

    async function getPresignedUrl(key) {
      // Presign API: API Gateway → Lambda → signed S3 GetObject URL (ap-south-1).
      const endpoint = 'https://xuk937xqpa.execute-api.ap-south-1.amazonaws.com';
      const res = await fetch(endpoint + '?key=' + encodeURIComponent(key));
      if (!res.ok) throw new Error('presign failed: ' + res.status);
      const json = await res.json();
      const url = json.url || json.presignedUrl || json.signedUrl;
      if (!url) throw new Error('presign response missing url');
      if (STUDIO_TESTING) console.log('[OATMEAL STUDIO][testing] presigned URL for', key, '\n', url);
      return url;
    }

    async function resolveAndRender(key) {
      try {
        const url = await getPresignedUrl(key);
        renderPreviewImage(url);
      } catch (err) {
        console.error('[OATMEAL STUDIO] resolve failed', key, err);
        resetPreview();
      }
    }

    let stateChangeTimer = null;

    function onStateChange() {
      clearTimeout(stateChangeTimer);
      stateChangeTimer = setTimeout(() => {
        const frontKey = buildStudioKeyFromState(state, 'front');
        if (!frontKey) {
          resetPreview();
          return;
        }

        // ───────────────────────────────────────────────────────────────────────
        // The carousel shows two views (front/back) of the selected combo.
        // buildStudioKeyFromState(state, view) gives the REAL per-view S3 keys.
        // ───────────────────────────────────────────────────────────────────────
        if (STUDIO_TESTING) {
          // TESTING: S3 has one sample render — show it for BOTH views and log the URL.
          console.log('[OATMEAL STUDIO][testing] selection →', {
            upper: state.upper?.variant?.sku,
            bottom: state.bottom?.variant?.sku,
            skin: state.skinTone,
            body: state.bodyShape
          }, '— showing sample:', STUDIO_TEST_KEY);
          resolveAndRenderViews([STUDIO_TEST_KEY, STUDIO_TEST_KEY]);
          return;
        }

        // GO-LIVE: real per-view renders from S3 (one image per front/back).
        resolveAndRenderViews(STUDIO_VIEWS.map(function (v) { return buildStudioKeyFromState(state, v); }));
      }, 120);
    }

    function buildStudioKeyFromState(st, view) {
      if (!st.skinTone || !st.bodyShape || !st.upper?.variant || !st.bottom?.variant) return null;

      const uSku = st.upper?.variant?.sku;
      const lSku = st.bottom?.variant?.sku;
      if (!uSku || !lSku) return null;

      // Garment "look" key = SKU minus its trailing size segment. Size is dropped because
      // it equals bodyShape (appended separately below) and would go stale on a body-shape
      // change; colour stays in the SKU (bound to the variant, so it's always current).
      const stripSize = (sku) => String(sku).split('_').slice(0, -1).join('_');
      const upper = stripSize(uSku);
      const lower = stripSize(lSku);

      // studio/v1/upper-<SKU-size>__lower-<SKU-size>__skin-<1..5>__bodyshape-<XS..XL>__view-<front|back>.webp
      // Each upper+lower+skin+body combo has TWO rendered images (front & back); the
      // __view- segment selects which. Save both in S3 under these keys.
      return 'studio/v1/upper-' + upper + '__lower-' + lower +
        '__skin-' + st.skinTone + '__bodyshape-' + st.bodyShape +
        '__view-' + (view || 'front') + '.webp';
    }

    function getContainedImageRect(img, container) {
      const c = container.getBoundingClientRect();
      const iW = img.naturalWidth;
      const iH = img.naturalHeight;
      if (!iW || !iH) return null;

      const imageBox = img.getBoundingClientRect();
      const boxWidth = imageBox.width || img.clientWidth || c.width;
      const boxHeight = imageBox.height || img.clientHeight || c.height;
      const boxLeft = imageBox.left - c.left;
      const boxTop = imageBox.top - c.top;

      const containerRatio = boxWidth / boxHeight;
      const imageRatio = iW / iH;

      let width, height, left, top;

      if (imageRatio > containerRatio) {
        width = boxWidth;
        height = boxWidth / imageRatio;
        left = boxLeft;
        top = boxTop + (boxHeight - height) / 2;
      } else {
        height = boxHeight;
        width = boxHeight * imageRatio;
        top = boxTop;
        left = boxLeft + (boxWidth - width) / 2;
      }

      return { left, top, width, height };
    }

    function getHotspotBreakpoint() {
      return window.matchMedia('(max-width: 749px)').matches ? 'mobile' : 'desktop';
    }

    function getHotspotLayout(type) {
      const breakpoint = getHotspotBreakpoint();
      return HOTSPOT_LAYOUTS[breakpoint][type];
    }

    function applyFixedHotspotCallout(hotspot, callout) {
      hotspot.style.setProperty('--lx', callout.lx + 'px');
      hotspot.style.setProperty('--ly', callout.ly + 'px');
      hotspot.style.setProperty('--line-lx', callout.lineLx + 'px');
      hotspot.style.setProperty('--line-ly', callout.lineLy + 'px');
    }

    function applyComputedHotspotCallout(hotspot, line, callout, rect, labelRect, dotPxX, dotPxY) {
      let lx;
      let ly;

      if (callout.direction === 'upper-left') {
        lx = -(labelRect.width + callout.gapX + callout.biasX);
        ly = -(labelRect.height + callout.gapY);
      } else {
        lx = callout.gapX + callout.biasX;
        ly = callout.gapY;
      }

      const labelLeft = dotPxX + lx;
      const labelTop = dotPxY + ly;
      const minLeft = callout.edgePad;
      const minTop = callout.edgePad;
      const maxLeft = rect.width - labelRect.width - callout.edgePad;
      const maxTop = rect.height - labelRect.height - callout.bottomPad;

      const clampedLeft = Math.max(minLeft, Math.min(maxLeft, labelLeft));
      const clampedTop = Math.max(minTop, Math.min(maxTop, labelTop));

      lx = clampedLeft - dotPxX;
      ly = clampedTop - dotPxY;

      hotspot.style.setProperty('--lx', Math.round(lx) + 'px');
      hotspot.style.setProperty('--ly', Math.round(ly) + 'px');

      if (!line) return;

      const lineWidth = line.getBoundingClientRect().width || 36;
      const anchorX = lx < 0
        ? lx + labelRect.width - callout.edgeInsetX
        : lx + callout.edgeInsetX;
      const anchorY = ly < 0
        ? ly + labelRect.height - callout.edgeInsetY
        : ly + callout.edgeInsetY;

      let lineLx = anchorX / 2;
      const lineLy = anchorY;
      const minLineHalfWidth = Math.max(8, Math.round(lineWidth / 2));

      if (Math.abs(lineLx) < minLineHalfWidth) {
        lineLx = (lineLx < 0 ? -1 : 1) * minLineHalfWidth;
      }

      hotspot.style.setProperty('--line-lx', Math.round(lineLx) + 'px');
      hotspot.style.setProperty('--line-ly', Math.round(lineLy) + 'px');
    }

    function positionDotFromImage(type) {
      const hotspot = elements.hotspots[type];
      const img = elements.previewImage;
      const preview = q('.oatmeal-studio__preview');
      if (!hotspot || !img || !preview) return;

      const layout = getHotspotLayout(type);
      const dot = layout.dot;
      if (dot.mode === 'fixed') {
        hotspot.style.setProperty('--x', String(dot.x));
        hotspot.style.setProperty('--y', String(dot.y));
        return;
      }

      const rect = getContainedImageRect(img, preview);
      if (!rect) return;

      const rule = HOTSPOT_DOT_RULES[dot.ruleKey];
      const x = rect.left + rect.width * rule.x;
      const y = rect.top + rect.height * rule.y;
      const percentX = (x / preview.clientWidth) * 100;
      const percentY = (y / preview.clientHeight) * 100;

      hotspot.style.setProperty('--x', percentX.toFixed(2));
      hotspot.style.setProperty('--y', percentY.toFixed(2));
    }

    function showToast(message) {
      const toast = elements.toast;
      if (!toast) return;
      toast.textContent = normalizeAvailabilityMessage(message);
      toast.classList.add('is-visible');
      setTimeout(() => toast.classList.remove('is-visible'), 3000);
    }

    function normalizeAvailabilityMessage(message) {
      if (typeof window.OatmealNormalizeAvailabilityMessage === 'function') {
        return window.OatmealNormalizeAvailabilityMessage(message);
      }

      const raw = String(message || '').trim();
      const lower = raw.toLowerCase();
      if (
        lower.includes('sold out') ||
        lower.includes('out of stock') ||
        lower.includes('unavailable') ||
        lower.includes('inventory') ||
        lower.includes('available quantity') ||
        lower.includes('maximum') ||
        lower.includes('selected size not available') ||
        lower.includes('size not available')
      ) {
        return 'This size or colour is not available right now.';
      }

      return raw || 'Could not add this item right now.';
    }

    function enableSelectButton(type) {
      const btn = q('[data-select-btn="' + type + '"]', elements.modals[type]);
      if (btn) btn.disabled = false;
    }

    function disableSelectButton(type) {
      const btn = q('[data-select-btn="' + type + '"]', elements.modals[type]);
      if (btn) btn.disabled = true;
    }

    function renderColorBarEmpty(type) {
      disableSelectButton(type);
      renderColorSwatches(type, null);
    }

    function updateBuyLookButton() {
      const btn = elements.buyLookBtn;
      if (!btn) return;

      if (state.upper.variant && state.bottom.variant) {
        const totalPrice = (state.upper.variant.price || 0) + (state.bottom.variant.price || 0);
        btn.textContent = 'ADD BOTH TO BOWL - ' + formatMoney(totalPrice);
        btn.disabled = false;
      } else {
        btn.textContent = 'ADD BOTH TO BOWL';
        btn.disabled = true;
      }
    }

    function updateTab(type) {
      const tab = q('[data-tab="' + type + '"]');
      if (!tab) return;

      const textSpan = q('[data-tab-text="' + type + '"]', tab);
      const swatchSpan = q('[data-tab-swatch="' + type + '"]', tab);
      const variant = state[type].variant;
      const hasSelection = !!(state[type].product && variant);

      tab.classList.toggle('is-selected', hasSelection);

      if (textSpan) {
        if (state[type].product) {
          textSpan.textContent = state[type].product.title;
        } else {
          textSpan.innerHTML = getDefaultTabLabel(type);
        }
      }

      if (swatchSpan) {
        if (!variant) {
          swatchSpan.style.display = 'none';
          return;
        }
        const hex = normalizeHex(variant.colour_hex) || '#ccc';
        swatchSpan.style.backgroundColor = hex;
        swatchSpan.style.display = 'inline-block';
      }
    }

    function openModal(type) {
      const committed = state[type];
      state.currentModal = type;
      state.selectedProduct = committed.product;
      state.pending[type].product = committed.product;
      state.pending[type].variant = committed.variant;

      const modal = elements.modals[type];
      if (!modal) return;

      lockPageScroll();
      modal.classList.add('is-open');
      state.filters[type] = { style: 'all', print: 'all' };
      state.page[type] = 1;

      renderStyleFilters(type);
      renderPrintFilters(type);
      renderProductGrid(type);
      renderColorSwatches(type, committed.product);

      requestAnimationFrame(() => {
        const grid = q('[data-product-grid="' + type + '"]', modal);
        if (!grid) return;
        grid.scrollTop = 0;

        const selectedCard = q('.oatmeal-studio__product-card.is-selected', grid);
        if (selectedCard) {
          const gridRect = grid.getBoundingClientRect();
          const cardRect = selectedCard.getBoundingClientRect();
          grid.scrollTop += cardRect.top - gridRect.top;
        }
      });

      if (committed.product && committed.variant) enableSelectButton(type);
      else disableSelectButton(type);
    }

    function closeModal(type) {
      const modal = elements.modals[type];
      if (!modal) return;
      modal.classList.remove('is-open');
      unlockPageScroll();
      state.pending[type].product = null;
      state.pending[type].variant = null;
      state.currentModal = null;
      state.selectedProduct = null;
    }

    function getProductTypeTags(product) {
      const rawType = String((product && product.product_type) || '').trim();
      if (rawType) {
        const parts = rawType.split(',');
        if (parts.length >= 2) {
          const derived = parts.slice(1).join(',').trim();
          if (derived) return [derived];
        }
      }

      const fallback = String((product && product.style) || '').trim();
      return fallback ? [fallback] : [];
    }

    function getBaseProductsForPrintOptions(type) {
      const all = productsData[type] || [];
      const f = state.filters[type];
      if (!f.style || f.style === 'all') return all;
      return all.filter(p => getProductTypeTags(p).includes(f.style));
    }

    function renderStyleFilters(type) {
      const modal = elements.modals[type];
      if (!modal) return;

      const el = q('[data-style-filters="' + type + '"]', modal);
      if (!el) return;

      const products = productsData[type] || [];
      const styles = Array.from(
        new Set(products.reduce((acc, product) => acc.concat(getProductTypeTags(product)), []))
      );

      const allBtn = '<button class="oatmeal-studio__pill ' + (state.filters[type].style === 'all' ? 'is-active' : '') + '" data-style="all">All</button>';
      const buttons = styles.map(s =>
        '<button class="oatmeal-studio__pill ' + (state.filters[type].style === s ? 'is-active' : '') + '" data-style="' + escapeAttr(s) + '">' + escapeHtml(s) + '</button>'
      ).join('');

      el.innerHTML = allBtn + buttons;
    }

    function renderPrintFilters(type) {
      const modal = elements.modals[type];
      if (!modal) return;

      const el = q('[data-print-filters="' + type + '"]', modal);
      if (!el) return;

      const base = getBaseProductsForPrintOptions(type);
      const prints = Array.from(new Set(base.map(p => (p.print_identity || '').trim()).filter(Boolean)));

      const allBtn = '<button class="oatmeal-studio__pill ' + (state.filters[type].print === 'all' ? 'is-active' : '') + '" data-print="all">All</button>';
      const buttons = prints.map(p =>
        '<button class="oatmeal-studio__pill ' + (state.filters[type].print === p ? 'is-active' : '') + '" data-print="' + escapeAttr(p) + '">' + escapeHtml(p) + '</button>'
      ).join('');

      el.innerHTML = allBtn + buttons;
    }

    function getVisibleProducts(type) {
      const all = productsData[type] || [];
      const f = state.filters[type];
      let out = all;

      if (f.style && f.style !== 'all') out = out.filter(p => getProductTypeTags(p).includes(f.style));
      if (f.print && f.print !== 'all') out = out.filter(p => String(p.print_identity || '').trim() === f.print);

      return out;
    }

    // One tile per colour-variant (not per product): each visible product is expanded
    // into one tile per unique colour (option1), shown with that colour's [vton] flat.
    // Colours with no [vton] flat are skipped entirely (Studio shows [vton] images only).
    function getColorTiles(type) {
      const visible = getVisibleProducts(type);
      const tiles = [];
      visible.forEach(product => {
        const seen = new Set();
        (product.variants || []).forEach(v => {
          const color = String(v.option1 || '').trim();
          const key = color.toLowerCase();
          if (!color || seen.has(key)) return;
          // [vton]-only: show a colour ONLY if it has its own [colour:]-matched [vton]
          // flat. No fallback to the product image — untagged colours are hidden.
          const flat = v.flat_image;
          if (!flat) return;
          seen.add(key);
          tiles.push({ product, color, flat });
        });
      });
      return tiles;
    }

    function renderColorTileCard(tile, type) {
      const pending = state.pending[type];
      const isSelected = !!(
        pending.product &&
        pending.variant &&
        String(pending.product.id) === String(tile.product.id) &&
        String(pending.variant.option1 || '').trim().toLowerCase() === String(tile.color).trim().toLowerCase()
      );

      return '' +
        '<div class="oatmeal-studio__product-card' + (isSelected ? ' is-selected' : '') + '" data-product-id="' + escapeAttr(tile.product.id) + '" data-color="' + escapeAttr(tile.color) + '">' +
          '<div class="oatmeal-studio__product-media">' +
            '<img class="oatmeal-studio__product-image" src="' + escapeAttr(tile.flat) + '" alt="' + escapeAttr(tile.product.title + ' – ' + tile.color) + '" loading="lazy" />' +
          '</div>' +
          '<div class="oatmeal-studio__product-info">' +
            '<p class="oatmeal-studio__product-title">' + escapeHtml(tile.product.title) + '</p>' +
          '</div>' +
        '</div>';
    }

    function renderProductGrid(type) {
      const modal = elements.modals[type];
      if (!modal) return;

      const grid = q('[data-product-grid="' + type + '"]', modal);
      if (!grid) return;

      grid.classList.add('is-fading');
      grid.classList.remove('is-ready');

      const tiles = getColorTiles(type);

      window.requestAnimationFrame(() => {
        if (!tiles.length) {
          grid.innerHTML = '<p style="text-align:center;color:#999;margin:16px 0;">No products found</p>';
          grid.classList.add('is-ready');
          return;
        }

        grid.innerHTML = tiles.map(tile => renderColorTileCard(tile, type)).join('');
        window.requestAnimationFrame(() => grid.classList.add('is-ready'));
      });
    }

    function pickVariantByColorAndSize(product, colorName, sizeName) {
      const wantColor = String(colorName || '').trim().toLowerCase();
      const wantSize = String(sizeName || '').trim().toLowerCase();
      const variants = product && product.variants ? product.variants : [];
      if (!variants.length) return null;

      const exact = variants.find(v => {
        const c = String(v.option1 || '').trim().toLowerCase();
        const s = String(v.option2 || '').trim().toLowerCase();
        return c === wantColor && s === wantSize;
      });
      if (exact) return exact;

      const colorOnly = variants.find(v => {
        const c = String(v.option1 || '').trim().toLowerCase();
        const s = String(v.option2 || '').trim().toLowerCase();
        return c === wantColor && (!s || s === 'null');
      });

      return colorOnly || null;
    }

    function selectProduct(type, productId) {
      const product = (productsData[type] || []).find(p => String(p.id) === String(productId));
      if (!product) return;

      state.selectedProduct = product;
      state.pending[type].product = product;
      state.pending[type].variant = null;
      const modal = elements.modals[type];
      const grid = modal && q('[data-product-grid="' + type + '"]', modal);
      if (grid) {
        qa('.oatmeal-studio__product-card', grid).forEach(c => c.classList.remove('is-selected'));
        const sel = grid.querySelector('[data-product-id="' + productId + '"]');
        if (sel) sel.classList.add('is-selected');
      }

      renderColorSwatches(type, product);
      disableSelectButton(type);
    }

    function selectVariant(type, variantId) {
      const product = state.selectedProduct || state.pending[type].product;
      if (!product) return;

      const variant = (product.variants || []).find(v => String(v.id) === String(variantId));
      if (!variant) return;

      state.pending[type].product = product;
      state.pending[type].variant = variant;

      renderColorSwatches(type, product);
      enableSelectButton(type);
    }

    // Tile click = pick product + colour at once, then resolve the SKU using the
    // already-selected body shape (= size). Replaces the old product-tile + colour-bar
    // two-step. The variant is resolved here; SELECT confirms it.
    function selectColorTile(type, productId, color, cardEl) {
      const product = (productsData[type] || []).find(p => String(p.id) === String(productId));
      if (!product) return;

      state.selectedProduct = product;
      state.pending[type].product = product;

      const variant = pickVariantByColorAndSize(product, color, state.bodyShape);
      if (!variant) {
        state.pending[type].variant = null;
        disableSelectButton(type);
        showToast('Size not available for this colour');
      } else {
        state.pending[type].variant = variant;
        enableSelectButton(type);
      }

      const modal = elements.modals[type];
      const grid = modal && q('[data-product-grid="' + type + '"]', modal);
      if (grid) {
        qa('.oatmeal-studio__product-card', grid).forEach(c => c.classList.remove('is-selected'));
        if (cardEl) cardEl.classList.add('is-selected');
      }
    }

    function confirmSelection(type) {
      const pending = state.pending[type];
      if (!pending?.product || !pending?.variant) return;
      state[type].product = pending.product;
      state[type].variant = pending.variant;
      onStateChange();
      updateTab(type);
      updateBuyLookButton();
      closeModal(type);
      refreshPreviewHotspots();
    }

    function renderColorSwatches(type, product) {
      const modal = elements.modals[type];
      if (!modal) return;

      const bar = q('[data-color-bar="' + type + '"]', modal);
      if (!bar) return;

      const palette = globalColors[type] || [];
      if (!palette.length) {
        bar.innerHTML = '<p class="oatmeal-studio__color-empty">No colours</p>';
        return;
      }

      const selectable = new Set();
      if (product) {
        (product.variants || []).forEach(v => {
          const picked = pickVariantByColorAndSize(product, v.option1, state.bodyShape);
          if (picked && picked.available !== false) {
            selectable.add(String(v.option1).trim().toLowerCase());
          }
        });
      }

      const selectedColor = product && state.pending[type].product && state.pending[type].variant &&
        String(state.pending[type].product.id) === String(product.id)
          ? String(state.pending[type].variant.option1).trim().toLowerCase()
          : null;

      bar.innerHTML = palette.map(c => {
        const name = c.name;
        const key = String(name).toLowerCase();
        const enabled = product && selectable.has(key);
        const selected = selectedColor === key;

        return '' +
          '<button class="oatmeal-studio__color-swatch ' +
            (selected ? 'is-selected ' : '') +
            (!c.hex ? 'is-unknown' : '') +
          '" style="background:' + (c.hex || 'transparent') + '" ' +
          (enabled ? '' : 'disabled') +
          ' data-color="' + escapeAttr(name) + '" title="' + escapeAttr(name) + '"></button>';
      }).join('');
    }

    function updateHotspot(type) {
      const hotspot = elements.hotspots[type];
      if (!hotspot) return;

      const preview = q('.oatmeal-studio__preview');
      if (!preview) return;

      const variant = state[type]?.variant;
      const product = state[type]?.product;
      const hasPreview = elements.previewImage && elements.previewImage.classList.contains('is-visible');
      const shouldShow = !!(hasPreview && variant && product);
      hotspot.classList.toggle('is-visible', shouldShow);
      if (!shouldShow) return;

      const priceEl = q('[data-hotspot-price="' + type + '"]', hotspot);
      if (priceEl) priceEl.textContent = formatHotspotMoney(variant);

      const label = q('.oatmeal-studio__hotspot-label', hotspot);
      if (!label) return;
      const line = q('.oatmeal-studio__hotspot-line', hotspot);
      const layout = getHotspotLayout(type);
      const callout = layout.callout;

      const rect = preview.getBoundingClientRect();
      const labelRect = label.getBoundingClientRect();
      const cs = getComputedStyle(hotspot);
      const x = parseFloat(cs.getPropertyValue('--x')) || 0;
      const y = parseFloat(cs.getPropertyValue('--y')) || 0;
      const dotPxX = (x / 100) * rect.width;
      const dotPxY = (y / 100) * rect.height;

      if (callout.mode === 'fixed') {
        applyFixedHotspotCallout(hotspot, callout);
        return;
      }

      applyComputedHotspotCallout(
        hotspot,
        line,
        callout,
        rect,
        labelRect,
        dotPxX,
        dotPxY
      );
    }

    async function addViaThemeForm(variantId, productUrl) {
      const base = String(productUrl || '').trim();
      if (!base) throw new Error('missing product url');

      const url = base + '?variant=' + encodeURIComponent(variantId);
      const res = await fetch(url, { credentials: 'same-origin' });
      if (!res.ok) throw new Error('fetch failed ' + res.status);

      const html = await res.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');

      const pfc =
        parsed.querySelector('product-form-component') ||
        (parsed.querySelector('form[action^="/cart/add"]') && parsed.querySelector('form[action^="/cart/add"]').closest('product-form-component'));

      if (!pfc) throw new Error('product-form-component not found on PDP HTML');

      const host = q('[data-studio-form-host]');
      if (!host) throw new Error('form host missing');

      host.innerHTML = '';
      host.appendChild(document.importNode(pfc, true));

      const form = q('product-form-component form', host) || q('form[action^="/cart/add"]', host);
      if (!form) throw new Error('theme form missing');

      const idInput = form.querySelector('input[name="id"]');
      if (!(idInput instanceof HTMLInputElement)) throw new Error('id input missing');

      idInput.value = String(variantId);
      form.dataset.oatCartSource = 'VTON';

      let attributionInput = form.querySelector('input[name="properties[_Oatmeal Sale Type]"]');
      if (!(attributionInput instanceof HTMLInputElement)) {
        attributionInput = document.createElement('input');
        attributionInput.type = 'hidden';
        attributionInput.name = 'properties[_Oatmeal Sale Type]';
        form.appendChild(attributionInput);
      }
      attributionInput.value = 'VTON';

      if (form.requestSubmit) form.requestSubmit();
      else form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }

    function resolveCartVariant(type) {
      const t = String(type || '').trim().toLowerCase();
      if (t !== 'upper' && t !== 'bottom') return null;

      const bucket = state[t];
      if (!bucket) return null;

      const product = bucket.product;
      const selected = bucket.variant;
      if (!product || !selected) return null;

      const desiredSize = state.bodyShape;
      const desiredColor = selected.option1;
      const picked = pickVariantByColorAndSize(product, desiredColor, desiredSize);
      if (!picked || picked.available === false) return null;

      return { id: picked.id, url: product.url };
    }

    async function buyLook() {
      const u = resolveCartVariant('upper');
      const b = resolveCartVariant('bottom');

      if (!u || !b) {
        showToast('Selected size not available for this colour.');
        return;
      }

      try {
        await addViaThemeForm(u.id, u.url);
        await addViaThemeForm(b.id, b.url);
        showToast('Added to cart!');
      } catch (e) {
        console.error('[OATMEAL STUDIO] theme add failed', e);
        showToast(e.message || 'Error adding to cart.');
      }
    }

    async function addHotspotItem(type) {
      const v = resolveCartVariant(type);
      if (!v) {
        showToast('Selected size not available for this colour.');
        return;
      }

      try {
        await addViaThemeForm(v.id, v.url);
        showToast('Added to bowl!');
      } catch (e) {
        console.error('[OATMEAL STUDIO] hotspot add failed', e);
        showToast(e.message || 'Error adding to bowl.');
      }
    }

    root.addEventListener('click', function (e) {
      const themeToggle = e.target.closest('[data-studio-theme-toggle]');
      if (themeToggle && root.contains(themeToggle)) {
        e.preventDefault();
        state.isDarkMode = !state.isDarkMode;
        syncThemeMode();
        updateHotspot('upper');
        updateHotspot('bottom');
        return;
      }

      const dot = e.target.closest('[data-preview-dot]');
      if (dot && root.contains(dot)) {
        e.preventDefault();
        setPreviewView(parseInt(dot.getAttribute('data-preview-dot'), 10) || 0);
        return;
      }

      const previewDirection = e.target.closest('[data-preview-direction]');
      if (previewDirection && root.contains(previewDirection)) {
        e.preventDefault();
        setPreviewView(previewCarousel.index + Number(previewDirection.getAttribute('data-preview-direction')));
        return;
      }

      const previewTrigger = e.target.closest('[data-preview-image]');
      if (previewTrigger && root.contains(previewTrigger)) {
        e.preventDefault();
        if (previewSwiped) { previewSwiped = false; return; }
        openPreviewViewer();
        return;
      }

      const tab = e.target.closest('[data-tab]');
      if (tab && root.contains(tab)) {
        e.preventDefault();
        openModal(String(tab.getAttribute('data-tab') || '').trim());
        return;
      }

      const tone = e.target.closest('[data-skin-tone]');
      if (tone && root.contains(tone)) {
        e.preventDefault();
        state.skinTone = String(tone.getAttribute('data-skin-tone') || '1');
        qa('[data-skin-tone]').forEach(x => x.classList.remove('is-active'));
        tone.classList.add('is-active');
        onStateChange();
        return;
      }

      const shape = e.target.closest('[data-body-shape]');
      if (shape && root.contains(shape)) {
        e.preventDefault();
        state.bodyShape = String(shape.getAttribute('data-body-shape') || 'M');
        qa('[data-body-shape]').forEach(x => x.classList.remove('is-active'));
        shape.classList.add('is-active');

        if (state.currentModal) {
          state.pending[state.currentModal].variant = null;
          disableSelectButton(state.currentModal);
          renderColorSwatches(state.currentModal, state.selectedProduct);
        }
        onStateChange();
        return;
      }

      const hotspotAdd = e.target.closest('[data-hotspot-add]');
      if (hotspotAdd && root.contains(hotspotAdd)) {
        e.preventDefault();
        e.stopPropagation();
        addHotspotItem(String(hotspotAdd.getAttribute('data-hotspot-add') || '').trim().toLowerCase());
        return;
      }

      const buyBtn = e.target.closest('[data-buy-look]');
      if (buyBtn && root.contains(buyBtn)) {
        e.preventDefault();
        if (!buyBtn.disabled) buyLook();
      }
    });

    document.addEventListener('click', function (e) {
      const viewerCloseBtn = e.target.closest('[data-viewer-close]');
      if (viewerCloseBtn) {
        e.preventDefault();
        closePreviewViewer();
        return;
      }

      const viewerZoomIn = e.target.closest('[data-viewer-zoom-in]');
      if (viewerZoomIn) {
        e.preventDefault();
        zoomViewer(1);
        return;
      }

      const viewerZoomOut = e.target.closest('[data-viewer-zoom-out]');
      if (viewerZoomOut) {
        e.preventDefault();
        zoomViewer(-1);
        return;
      }

      if (elements.previewViewer && e.target === elements.previewViewer) {
        e.preventDefault();
        closePreviewViewer();
        return;
      }

      const closeBtn = e.target.closest('[data-modal-close]');
      if (closeBtn) {
        e.preventDefault();
        closeModal(String(closeBtn.getAttribute('data-modal-close') || '').trim());
        return;
      }

      const modalEl = e.target.closest('[data-modal]');
      if (modalEl && e.target === modalEl) {
        e.preventDefault();
        closeModal(String(modalEl.getAttribute('data-modal') || '').trim());
        return;
      }

      const selectBtn = e.target.closest('[data-select-btn]');
      if (selectBtn) {
        e.preventDefault();
        confirmSelection(String(selectBtn.getAttribute('data-select-btn') || '').trim());
        return;
      }

      const styleBtn = e.target.closest('[data-style]');
      if (styleBtn) {
        e.preventDefault();
        const type = state.currentModal;
        if (!type) return;
        state.filters[type].style = String(styleBtn.getAttribute('data-style') || 'all');
        state.filters[type].print = 'all';
        state.page[type] = 1;
        state.selectedProduct = null;
        state.pending[type].product = null;
        state.pending[type].variant = null;
        renderStyleFilters(type);
        renderPrintFilters(type);
        renderProductGrid(type);
        renderColorBarEmpty(type);
        return;
      }

      const printBtn = e.target.closest('[data-print]');
      if (printBtn) {
        e.preventDefault();
        const type = state.currentModal;
        if (!type) return;
        state.filters[type].print = String(printBtn.getAttribute('data-print') || 'all');
        state.page[type] = 1;
        state.selectedProduct = null;
        state.pending[type].product = null;
        state.pending[type].variant = null;
        renderPrintFilters(type);
        renderProductGrid(type);
        renderColorBarEmpty(type);
        return;
      }

      const card = e.target.closest('.oatmeal-studio__product-card');
      if (card) {
        e.preventDefault();
        const type = state.currentModal;
        if (!type) return;
        selectColorTile(
          type,
          String(card.getAttribute('data-product-id')),
          String(card.getAttribute('data-color') || ''),
          card
        );
        return;
      }
    });

    document.addEventListener('keydown', function (e) {
      if (viewerState.open) {
        if (e.key === 'Escape') {
          e.preventDefault();
          closePreviewViewer();
          return;
        }
        if (e.key === '+' || e.key === '=') {
          e.preventDefault();
          zoomViewer(1);
          return;
        }
        if (e.key === '-' || e.key === '_') {
          e.preventDefault();
          zoomViewer(-1);
          return;
        }
      }
      if (e.key === 'Escape' && state.currentModal) closeModal(state.currentModal);
    });

    root.addEventListener('keydown', function (e) {
      const previewTrigger = e.target.closest('[data-preview-image]');
      if (!previewTrigger || !root.contains(previewTrigger)) return;
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      openPreviewViewer();
    });

    if (elements.viewerImage) {
      elements.viewerImage.addEventListener('load', function () {
        if (viewerState.open) {
          fitViewerImageToStage();
          resetViewerTransform();
        }
      });
    }

    // Thumb-swipe on the preview to slide between front/back views. A horizontal
    // drag past the threshold switches view and suppresses the trailing click so it
    // doesn't open the zoom viewer; a tap (small movement) still opens zoom.
    (function bindPreviewSwipe() {
      const preview = elements.previewImage && elements.previewImage.closest('.oatmeal-studio__preview');
      if (!preview) return;
      let x0 = 0, y0 = 0, tracking = false;
      const THRESHOLD = 30;

      // Kill native image drag-and-drop (desktop), which otherwise eats the swipe.
      preview.addEventListener('dragstart', function (e) { e.preventDefault(); });

      preview.addEventListener('pointerdown', function (e) {
        if (previewCarousel.urls.length <= 1) return;
        tracking = true;
        x0 = e.clientX;
        y0 = e.clientY;
      });

      preview.addEventListener('pointerup', function (e) {
        if (!tracking) return;
        tracking = false;
        const dx = e.clientX - x0;
        const dy = e.clientY - y0;
        if (Math.abs(dx) > THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
          previewSwiped = true;
          setPreviewView(previewCarousel.index + (dx < 0 ? 1 : -1));
        }
      });

      preview.addEventListener('pointercancel', function () { tracking = false; });
    })();

    if (elements.viewerStage) {
      elements.viewerStage.addEventListener('wheel', function (e) {
        if (!viewerState.open) return;
        e.preventDefault();
        zoomViewer(e.deltaY < 0 ? 1 : -1);
      }, { passive: false });

      elements.viewerStage.addEventListener('pointerdown', function (e) {
        if (!viewerState.open) return;
        elements.viewerStage.setPointerCapture?.(e.pointerId);
        viewerState.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

        if (viewerState.pointers.size === 1 && viewerState.scale > 1) {
          viewerState.dragOriginX = e.clientX - viewerState.x;
          viewerState.dragOriginY = e.clientY - viewerState.y;
          elements.viewerStage.classList.add('is-dragging');
        }

        if (viewerState.pointers.size === 2) {
          viewerState.pinchDistance = getPointerDistance(Array.from(viewerState.pointers.values()));
          viewerState.pinchScale = viewerState.scale;
          elements.viewerStage.classList.remove('is-dragging');
        }
      });

      elements.viewerStage.addEventListener('pointermove', function (e) {
        if (!viewerState.open || !viewerState.pointers.has(e.pointerId)) return;
        viewerState.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

        if (viewerState.pointers.size === 2) {
          const distance = getPointerDistance(Array.from(viewerState.pointers.values()));
          if (viewerState.pinchDistance > 0) {
            setViewerScale(viewerState.pinchScale * (distance / viewerState.pinchDistance));
          }
          return;
        }

        if (viewerState.pointers.size === 1 && viewerState.scale > 1) {
          viewerState.x = e.clientX - viewerState.dragOriginX;
          viewerState.y = e.clientY - viewerState.dragOriginY;
          applyViewerTransform();
        }
      });

      function releaseViewerPointer(e) {
        if (!viewerState.pointers.has(e.pointerId)) return;
        viewerState.pointers.delete(e.pointerId);

        if (viewerState.pointers.size < 2) {
          viewerState.pinchDistance = 0;
        }

        if (viewerState.pointers.size === 1 && viewerState.scale > 1) {
          const remaining = Array.from(viewerState.pointers.values())[0];
          viewerState.dragOriginX = remaining.x - viewerState.x;
          viewerState.dragOriginY = remaining.y - viewerState.y;
          elements.viewerStage.classList.add('is-dragging');
          return;
        }

        elements.viewerStage.classList.remove('is-dragging');
      }

      elements.viewerStage.addEventListener('pointerup', releaseViewerPointer);
      elements.viewerStage.addEventListener('pointercancel', releaseViewerPointer);
      elements.viewerStage.addEventListener('pointerleave', function (e) {
        if (e.pointerType === 'mouse') releaseViewerPointer(e);
      });
    }

    function renderAll(type) {
      renderStyleFilters(type);
      renderPrintFilters(type);
      renderProductGrid(type);
      renderColorBarEmpty(type);
      disableSelectButton(type);
    }

    function initAutoSelection() {
      const pre = root.dataset.preselectedVariant || root.getAttribute('data-preselected-variant');
      applyPreselect(pre);
    }

    function clearSelection() {
      state.upper.product = null;
      state.upper.variant = null;
      state.bottom.product = null;
      state.bottom.variant = null;
      state.pending.upper.product = null;
      state.pending.upper.variant = null;
      state.pending.bottom.product = null;
      state.pending.bottom.variant = null;
      state.currentModal = null;
      state.selectedProduct = null;
      const upperTabText = q('[data-tab="upper"] [data-tab-text="upper"]');
      const bottomTabText = q('[data-tab="bottom"] [data-tab-text="bottom"]');
      if (upperTabText) upperTabText.innerHTML = getDefaultTabLabel('upper');
      if (bottomTabText) bottomTabText.innerHTML = getDefaultTabLabel('bottom');

      const upperSw = q('[data-tab="upper"] [data-tab-swatch="upper"]');
      const bottomSw = q('[data-tab="bottom"] [data-tab-swatch="bottom"]');
      if (upperSw) upperSw.style.display = 'none';
      if (bottomSw) bottomSw.style.display = 'none';

      const upperTab = q('[data-tab="upper"]');
      const bottomTab = q('[data-tab="bottom"]');
      if (upperTab) upperTab.classList.remove('is-selected');
      if (bottomTab) bottomTab.classList.remove('is-selected');

      updateBuyLookButton();
      resetPreview();
    }

    function applyPreselect(preselect) {
      const detail =
        preselect && typeof preselect === 'object'
          ? preselect
          : { variantId: preselect };
      const shouldReset = detail.reset !== false;

      const idStr = String(detail.variantId || '').trim();
      const productIdStr = String(detail.productId || '').trim();
      const hintedCategory = String(detail.category || inferCategoryFromProduct(detail.product) || '').trim();

      if (shouldReset) {
        clearSelection();
      }

      if (!idStr && !productIdStr && !detail.product) {
        return;
      }

      let foundProduct = null;
      let foundVariant = null;
      let category = null;

      const searchOrder = hintedCategory === 'upper'
        ? ['upper', 'bottom']
        : hintedCategory === 'bottom'
          ? ['bottom', 'upper']
          : ['upper', 'bottom'];

      searchOrder.some(cat => {
        const bucket = productsData[cat] || [];
        let productMatch = null;

        if (productIdStr) {
          productMatch = bucket.find(p => String(p.id) === productIdStr) || null;
        }

        if (productMatch) {
          const v = (productMatch.variants || []).find(x => String(x.id) === idStr);
          if (!v) return false;
          foundProduct = productMatch;
          foundVariant = v;
          category = cat;
          return true;
        }

        return bucket.some(p => {
          const v = (p.variants || []).find(x => String(x.id) === idStr);
          if (!v) return false;
          foundProduct = p;
          foundVariant = v;
          category = cat;
          return true;
        });
      });

      if (!foundProduct && detail.product) {
        const injectedCategory = hintedCategory || inferCategoryFromProduct(detail.product);
        const injectedProduct = ensureInjectedProduct(injectedCategory, detail.product);
        if (injectedProduct) {
          const v = (injectedProduct.variants || []).find(x => String(x.id) === idStr);
          if (v) {
            foundProduct = injectedProduct;
            foundVariant = v;
            category = injectedCategory;
          }
        }
      }

      if (!foundProduct || !foundVariant || !category) return;

      state[category].product = foundProduct;
      state[category].variant = foundVariant;

      if (foundVariant.option2) {
        state.bodyShape = String(foundVariant.option2);
        qa('[data-body-shape]').forEach(x => x.classList.remove('is-active'));
        const b = q('[data-body-shape="' + escapeAttr(state.bodyShape) + '"]');
        if (b) b.classList.add('is-active');
      }

      updateTab(category);
      updateBuyLookButton();
      onStateChange();
      refreshPreviewHotspots();
    }

    document.addEventListener('oatmeal:selectVariant', function (e) {
      const detail = e.detail || {};
      if (!detail.variantId && !detail.product && !detail.productId) return;
      if (detail.variantId) root.dataset.preselectedVariant = String(detail.variantId);
      if (detail.productId) root.dataset.preselectedProductId = String(detail.productId);
      applyPreselect(detail);
    });

    root.__oatStudioApplyPreselect = applyPreselect;
    root.__oatStudioClear = clearSelection;

    mountModalsToBody();
    syncThemeMode();
    setPreviewImageInteractive(false);
    renderAll('upper');
    renderAll('bottom');
    initAutoSelection();
    updateBuyLookButton();

    let hotspotRefreshRaf = 0;
    function scheduleHotspotRefresh() {
      if (hotspotRefreshRaf) cancelAnimationFrame(hotspotRefreshRaf);
      hotspotRefreshRaf = requestAnimationFrame(() => {
        hotspotRefreshRaf = 0;
        refreshPreviewHotspots();
        if (viewerState.open) {
          fitViewerImageToStage();
          applyViewerTransform();
        }
      });
    }

    window.addEventListener('resize', scheduleHotspotRefresh);
    window.addEventListener('orientationchange', scheduleHotspotRefresh);
  };

  function autoMountAll() {
    document.querySelectorAll('.oatmeal-studio[data-section-id]').forEach(function (el) {
      window.OatmealStudioMount(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoMountAll);
  } else {
    autoMountAll();
  }
})();
